import { FormEvent, useEffect, useState } from "react";
import { kpsiGet, kpsiPost } from "./kpsiApi";

type Company = { id: number; name: string };

interface Props {
  id?: number;
  onBack: () => void;
  onSaved: () => void;
}

export default function KpsiUserEdit({ id, onBack, onSaved }: Props) {
  const isNew = !id;
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayEmail, setDisplayEmail] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [interpreter, setInterpreter] = useState<Set<number>>(new Set());
  const [scorer, setScorer] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      // New operator: still need company list — load via a lightweight path by creating empty get after save only.
      // Fetch companies from bootstrap-like company list through Operators requires id; use Companies list for admin.
      void kpsiGet<{ rows: Company[] }>("/Kpsi/Companies")
        .then((data) => setCompanies((data.rows ?? []).map((c) => ({ id: c.id, name: c.name }))))
        .catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
      return;
    }
    void kpsiGet<{
      user: { id: number; email?: string; firstName: string; lastName: string };
      companies: Company[];
      interpreterCompanyIds: number[];
      scorerCompanyIds: number[];
    }>(`/Kpsi/Operators/${id}`)
      .then((data) => {
        setDisplayName(`${data.user.firstName} ${data.user.lastName}`.trim());
        setDisplayEmail(data.user.email ?? "");
        setCompanies(data.companies ?? []);
        setInterpreter(new Set(data.interpreterCompanyIds ?? []));
        setScorer(new Set(data.scorerCompanyIds ?? []));
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [id]);

  function toggle(set: Set<number>, id: number, updater: (next: Set<number>) => void) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    updater(next);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await kpsiPost("/Kpsi/Operators", {
        rightTimeUserId: id ?? null,
        email: isNew ? email : undefined,
        firstName: isNew ? firstName : undefined,
        lastName: isNew ? lastName : undefined,
        interpreterCompanyIds: [...interpreter],
        scorerCompanyIds: [...scorer],
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <>
      <p><button type="button" onClick={onBack}>&laquo; Back to users</button></p>
      <h1>{isNew ? "Add a KPSI Operator" : "Configure KPSI Access"}</h1>
      <p className="kpsi-muted">
        Password and profile changes use the normal RightTime user screens. Here you only set Interpreter / Scorer companies.
      </p>
      {error && <div className="kpsi-alert">{error}</div>}

      <form onSubmit={(e) => void onSubmit(e)}>
        {isNew ? (
          <div className="kpsi-form-row">
            <div>
              <label>First name *</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div>
              <label>Last name *</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
            <div>
              <label>Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <div className="kpsi-muted">Creates or links a RightTime user and grants KPSI Access. They set their password via invite / first login.</div>
            </div>
          </div>
        ) : (
          <div className="kpsi-form-row">
            <div>
              <label>Name</label>
              <input value={displayName} disabled readOnly />
            </div>
            <div>
              <label>Email</label>
              <input value={displayEmail} disabled readOnly />
            </div>
          </div>
        )}

        <div className="kpsi-form-row">
          <div>
            <h2 className="h6">Interpreter</h2>
            <div className="kpsi-checkbox-list">
              {companies.map((c) => (
                <label key={`i-${c.id}`}>
                  <input
                    type="checkbox"
                    checked={interpreter.has(c.id)}
                    onChange={() => toggle(interpreter, c.id, setInterpreter)}
                  />{" "}
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h2 className="h6">Scorer</h2>
            <div className="kpsi-checkbox-list">
              {companies.map((c) => (
                <label key={`s-${c.id}`}>
                  <input
                    type="checkbox"
                    checked={scorer.has(c.id)}
                    onChange={() => toggle(scorer, c.id, setScorer)}
                  />{" "}
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        <button type="submit">{isNew ? "Add" : "Update"} &raquo;</button>
      </form>
    </>
  );
}
